import { useEffect, useState } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import language from '../../variables/language';
import DateInput from '../../components/forms/DateInput';
import TimeInput from '../../components/forms/TimeInput';
import Selector from '../../components/forms/Selector';
import IconCycled from '../../components/icons/IconCycled';
import '../../css/modals.css';

const emptyValues = () => ({ start: '', end: '', date: '', price: '', duration: '', groupe: '' });

// The "cycled" toggle on the schedule page shows a fixed template week
// (backend stores recurring lessons on placeholder 1950-xx-xx dates - see
// LessonController.saveOne's `date.contains("1950")` auto-copy-to-current-
// week logic). Adding a lesson while that toggle is on therefore can't use
// a real calendar date - it has to land on one of that template week's 7
// existing placeholder dates (picked here by weekday), with isCycled
// forced to 1. This mirrors exactly what the old Index.jsx's cycled-mode
// `inputs` config used to do, just as its own modal instead of a dropdown
// panel bolted onto the page header.
export default function ScheduleLessonCreateModal({ isOpen, onClose, onCreated, cycled }) {
   const navigate = useNavigate();
   const [values, setValues] = useState(emptyValues());
   const [saving, setSaving] = useState(false);
   const [cycledDays, setCycledDays] = useState([]);
   const lang = language[language.lang];

   useEffect(() => {
      if (!isOpen) return;
      setValues(emptyValues());
      if (cycled) {
         axios.get('/api/schedule?cycled=1', { withCredentials: true })
            .then(response => setCycledDays(Array.isArray(response.data) ? response.data : []))
            .catch(() => setCycledDays([]));
      }
   }, [isOpen, cycled]);

   if (!isOpen) return '';

   const stopProp = e => e.stopPropagation();
   const handleFieldChange = (name, value) => setValues(prev => ({ ...prev, [name]: value }));

   const handleClose = () => {
      setValues(emptyValues());
      onClose();
   };

   const handleSubmit = (e) => {
      e.preventDefault();
      if (saving) return;
      setSaving(true);
      const payload = {};
      Object.keys(values).forEach(key => {
         if (values[key] !== '') payload[key] = values[key];
      });
      if (cycled) payload.isCycled = '1';
      axios.post('/api/lessons', JSON.stringify(payload), {
         withCredentials: true,
         headers: { 'Content-Type': 'application/json' },
      }).then(() => {
         setValues(emptyValues());
         onCreated();
      }).catch(err => {
         if (err.response && err.response.status === 401) navigate('/login');
         console.log(err);
      }).finally(() => setSaving(false));
   };

   return (
      <div className='modal-update' onClick={handleClose} style={{ display: 'flex' }}>
         <div className='modal-window' onClick={stopProp}>
            <h3 className='modal-w-title'>{lang.modals.lessons.createTitle}</h3>
            <form onSubmit={handleSubmit}>
               {cycled ? (
                  <div className='schedule-create-cycled-note'>
                     <IconCycled fillColor='icon-red' />
                     {lang.scheduleCreateCycledNote}
                  </div>
               ) : ''}
               <div className='schedule-create-fields'>
                  <div className='flex-from-input-wraper' data-label={lang.start}>
                     <TimeInput value={values.start} onChange={v => handleFieldChange('start', v)} />
                  </div>
                  <div className='flex-from-input-wraper' data-label={lang.end}>
                     <TimeInput value={values.end} onChange={v => handleFieldChange('end', v)} />
                  </div>
                  {cycled ? (
                     <div className='flex-from-input-wraper schedule-create-field-full' data-label={lang.date}>
                        <select className='submit-field form-selection txt-c' value={values.date}
                           onChange={e => handleFieldChange('date', e.target.value)}>
                           <option value=''>{lang['not selected']}</option>
                           {cycledDays.map(d => {
                              const dt = DateTime.fromFormat(d.date, 'yyyy-MM-dd');
                              return <option key={d.date} value={d.date}>{dt.isValid ? dt.toFormat('cccc') : d.date}</option>;
                           })}
                        </select>
                     </div>
                  ) : (
                     <div className='flex-from-input-wraper schedule-create-field-full' data-label={lang.date}>
                        <DateInput value={values.date} onChange={v => handleFieldChange('date', v)} />
                     </div>
                  )}
                  <div className='flex-from-input-wraper' data-label={lang.price}>
                     <input className='submit-field from-input txt-c' type='number' step='0.01'
                        value={values.price} onChange={e => handleFieldChange('price', e.target.value)} />
                  </div>
                  <div className='flex-from-input-wraper' data-label={lang.duration}>
                     <input className='submit-field from-input txt-c' type='number' step='0.01'
                        value={values.duration} onChange={e => handleFieldChange('duration', e.target.value)} />
                  </div>
                  <div className='flex-from-input-wraper schedule-create-field-full' data-label={lang.groupe}>
                     <Selector className='submit-field form-selection txt-c' name='groupe' url='/api/groups'
                        value={values.groupe} disabled={false}
                        selectorsHundler={(value) => handleFieldChange('groupe', value)} />
                  </div>
               </div>
               <button type='submit' className='form-button txt-c' disabled={saving} style={{ marginTop: 14 }}>
                  {saving ? lang['Loading ...'] : lang.save}
               </button>
            </form>
         </div>
      </div>
   );
}
